import {useStoredMaterialIdsFromHistoryQuery} from '@domain/material-storage'
import {MaterialType} from '@domain/materials'
import {dataTestId} from '@hconnect/uikit'
import {Close} from '@mui/icons-material'
import {BoxProps, Collapse, IconButton, Stack} from '@mui/material'
import {toPixel, usePlanningChartStartEnd} from '@widgets/planning-schedule-chart'
import {
  SelectMaterialIdDropdown,
  SelectMaterialTypeDropdown,
  StockDevelopmentChartLegend
} from '@widgets/stock-card'
import {useCallback, useEffect, useMemo, useState} from 'react'
import {useTranslation} from 'react-i18next'

import {FEATURE_NAMESPACE, ProductionPlanningLSSettingsKeys} from '../../../consts'
import {
  Timeframe,
  useLocalStorageSettings,
  useSearchParams,
  useStoredMaterialTypesFromHistoryQuery
} from '../../../hooks'
import {CollapsibleCard} from '../../shared'
import {ProductionPlanningSkeleton} from '../../shared/skeletons'

import {PlanningStockDevelopmentChartContainer} from './detail'
import {StockLevelsOverviewSubtitle} from './overview'
import {SilosGridSection} from './SilosGridSection'

type StockLevelsCardProps = BoxProps & {
  plantCode: string
}

export const StockLevelsCard = ({plantCode, ...props}: StockLevelsCardProps) => {
  const {t} = useTranslation(FEATURE_NAMESPACE)

  const {startOfPlan, endOfPlan} = usePlanningChartStartEnd()

  const {settings: siloSectionSettings, updateSettings: setSiloSectionSettings} =
    useLocalStorageSettings(ProductionPlanningLSSettingsKeys.PlanningSiloSectionSettings, {
      isSiloSectionOpen: true
    })
  const {isSiloSectionOpen} = siloSectionSettings

  const selectedTimeframe: Timeframe = useMemo(
    () => [startOfPlan, endOfPlan],
    [startOfPlan, endOfPlan]
  )

  const toggleIsMinimized = useCallback(
    () => setSiloSectionSettings({isSiloSectionOpen: !isSiloSectionOpen}),
    [isSiloSectionOpen, setSiloSectionSettings]
  )

  const {data: storedMaterialTypes, isLoading: storedMaterialTypesIsLoading} =
    useStoredMaterialTypesFromHistoryQuery({timeFrame: selectedTimeframe})
  const [selectedMaterialType, changeSelectedMaterialType] = useState<MaterialType | 'all_types'>(
    'all_types'
  )

  const {data: allStoredMaterialIds, isLoading: allStoredMaterialIdsIsLoading} =
    useStoredMaterialIdsFromHistoryQuery({
      timeFrame: selectedTimeframe
    })

  const isLoading = allStoredMaterialIdsIsLoading || storedMaterialTypesIsLoading

  const [obtainedSearchParams, setSearchParams] = useSearchParams('materialId')
  const selectedMaterialId = obtainedSearchParams.materialId
    ? Number(obtainedSearchParams.materialId).valueOf() || null
    : null
  // filter only loaded material ids
  const filteredMaterialId =
    selectedMaterialId && allStoredMaterialIds?.includes(selectedMaterialId)
      ? selectedMaterialId
      : null

  useEffect(() => {
    if (allStoredMaterialIdsIsLoading) {
      return
    }
    const newSearchParam = filteredMaterialId !== null ? filteredMaterialId.toString() : null
    if (newSearchParam !== obtainedSearchParams.materialId) {
      setSearchParams({materialId: newSearchParam})
    }
  }, [setSearchParams, obtainedSearchParams, filteredMaterialId, allStoredMaterialIdsIsLoading])

  useEffect(() => {
    if (allStoredMaterialIdsIsLoading || storedMaterialTypesIsLoading) {
      return
    }
    if (
      selectedMaterialType !== 'all_types' &&
      !storedMaterialTypes?.includes(selectedMaterialType)
    ) {
      changeSelectedMaterialType('all_types')
    }
  }, [
    allStoredMaterialIdsIsLoading,
    selectedMaterialType,
    storedMaterialTypes,
    storedMaterialTypesIsLoading
  ])

  const changeSelectedMaterialId = useCallback(
    (materialId: number | undefined) => {
      setSearchParams({materialId: materialId ? materialId.toString() : null})
    },
    [setSearchParams]
  )

  const switchToOverview = useCallback(() => {
    changeSelectedMaterialId(undefined)
  }, [changeSelectedMaterialId])

  return (
    <CollapsibleCard
      {...props}
      isCollapsed={!isSiloSectionOpen}
      onToggleCollapsed={toggleIsMinimized}
      title={
        selectedMaterialId !== undefined ? t('stock.stockDevelopment') : t('planning.stockLevels')
      }
      subtitle={
        selectedMaterialId === undefined && <StockLevelsOverviewSubtitle lastUpdated={endOfPlan} />
      }
      headerContent={
        <Collapse in={isSiloSectionOpen}>
          {filteredMaterialId !== null ? (
            <Stack direction="row" justifyContent="end" alignItems="center" spacing={2}>
              <StockDevelopmentChartLegend />
              <SelectMaterialIdDropdown
                selectedMaterialId={filteredMaterialId}
                changeSelectedMaterialId={changeSelectedMaterialId}
                materialIds={allStoredMaterialIds || []}
              />
              <div>
                <IconButton
                  onClick={switchToOverview}
                  color="primary"
                  {...dataTestId('close_material_detail_button')}
                >
                  <Close />
                </IconButton>
              </div>
            </Stack>
          ) : (
            <Stack direction="row" justifyContent="end">
              <SelectMaterialTypeDropdown
                selectedMaterialType={selectedMaterialType}
                changeSelectedMaterialType={changeSelectedMaterialType}
                storedMaterialTypes={storedMaterialTypes || []}
              />
            </Stack>
          )}
        </Collapse>
      }
      {...dataTestId('stock_levels_card')}
    >
      <Collapse in={isSiloSectionOpen}>
        {isLoading ? (
          <ProductionPlanningSkeleton height={toPixel(430)} withPaper={false} />
        ) : filteredMaterialId !== null ? (
          <PlanningStockDevelopmentChartContainer materialId={filteredMaterialId} />
        ) : (
          <SilosGridSection
            plantCode={plantCode}
            onSelectMaterial={changeSelectedMaterialId}
            materialType={selectedMaterialType !== 'all_types' ? selectedMaterialType : undefined}
          />
        )}
      </Collapse>
    </CollapsibleCard>
  )
}
